import {useMemo, useState} from 'react'
import {FlatList, View} from 'react-native'
import {type AppBskyActorDefs} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useQuery} from '@tanstack/react-query'
import type {NativeStackScreenProps} from '@react-navigation/native-stack'

import {
  acceptRequest,
  declineRequest,
  listRequests,
} from '#/lib/private/PrivateAccountService'
import {type PrivateFollowRequest} from '#/lib/private/privateTypes'
import {type CommonNavigatorParams} from '#/lib/routes/types'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {useProfilesQuery} from '#/state/queries/profile'
import {useAgent, useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Layout from '#/components/Layout'
import * as ProfileCard from '#/components/ProfileCard'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'PrivateRequests'>

export function PrivateRequestsScreen({}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const agent = useAgent()
  const {currentAccount} = useSession()
  const moderationOpts = useModerationOpts()
  const [workingId, setWorkingId] = useState<string | null>(null)

  const {data: requests = [], refetch, isLoading} = useQuery({
    queryKey: ['private-requests', currentAccount?.did],
    queryFn: () => listRequests(agent, currentAccount?.did),
    enabled: !!currentAccount?.did,
    staleTime: 10 * 1000,
  })

  const requesters = useMemo(
    () => requests.map(request => request.fromDid),
    [requests],
  )

  const {data: profilesData} = useProfilesQuery({
    handles: requesters,
    maintainData: true,
  })

  const profilesByDid = useMemo(() => {
    const map = new Map<string, AppBskyActorDefs.ProfileView>()
    for (const profile of profilesData?.profiles || []) {
      map.set(profile.did, profile)
    }
    return map
  }, [profilesData?.profiles])

  const onAccept = async (request: PrivateFollowRequest) => {
    if (workingId) return
    setWorkingId(request.id)
    try {
      await acceptRequest(agent, request)
      await refetch()
    } finally {
      setWorkingId(null)
    }
  }

  const onDecline = async (request: PrivateFollowRequest) => {
    if (workingId) return
    setWorkingId(request.id)
    try {
      await declineRequest(agent, request)
      await refetch()
    } finally {
      setWorkingId(null)
    }
  }

  const renderRequest = ({item}: {item: PrivateFollowRequest}) => {
    const profile = profilesByDid.get(item.fromDid)
    if (!profile || !moderationOpts) {
      return null
    }
    return (
      <View style={[a.py_md, a.border_t, t.atoms.border_contrast_medium]}>
        <ProfileCard.Header>
          <ProfileCard.Avatar profile={profile} moderationOpts={moderationOpts} />
          <ProfileCard.NameAndHandle
            profile={profile}
            moderationOpts={moderationOpts}
          />
        </ProfileCard.Header>
        {!!item.message && (
          <Text style={[a.pt_sm, t.atoms.text_contrast_medium]}>
            {item.message}
          </Text>
        )}
        <View style={[a.flex_row, a.gap_sm, a.pt_sm]}>
          <Button
            color="primary"
            variant="solid"
            size="small"
            disabled={workingId === item.id}
            onPress={() => onAccept(item)}
            label={_(msg`Accept follow request`)}>
            <ButtonText>
              <Trans>Accept</Trans>
            </ButtonText>
          </Button>
          <Button
            color="secondary"
            variant="solid"
            size="small"
            disabled={workingId === item.id}
            onPress={() => onDecline(item)}
            label={_(msg`Decline follow request`)}>
            <ButtonText>
              <Trans>Decline</Trans>
            </ButtonText>
          </Button>
        </View>
      </View>
    )
  }

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Follow requests</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>
        {isLoading ? (
          <View style={[a.px_lg, a.py_lg]}>
            <Text style={[t.atoms.text_contrast_medium]}>
              <Trans>Loading requests...</Trans>
            </Text>
          </View>
        ) : requests.length === 0 ? (
          <View style={[a.px_lg, a.py_lg]}>
            <Text style={[t.atoms.text_contrast_medium]}>
              <Trans>No follow requests.</Trans>
            </Text>
          </View>
        ) : (
          <FlatList
            data={requests}
            keyExtractor={item => item.id}
            renderItem={renderRequest}
            contentContainerStyle={[a.px_lg, a.pb_4xl]}
          />
        )}
      </Layout.Content>
    </Layout.Screen>
  )
}
